import React from "react";
import { useTranslation } from "../../../hooks/useTranslation";
import { useInView } from "../../../hooks/useInView";
import ProgressiveImage from "../../Ui/ProgressiveImage/ProgressiveImage";
import aboutIntroPhoto from "../../../images/event.jpg";
import aboutStoryPhoto from "../../../images/london-park-autumn.jpg";

const AboutSection = () => {
  const { t } = useTranslation();
  // Low threshold + an expanding bottom rootMargin so each block starts its
  // reveal ~20% of a viewport before it scrolls into view — the fade/slide is
  // already finishing as it reaches the fold, instead of the block sitting
  // visibly blank (children are opacity:0 until .is-visible lands) for the
  // beat between "on screen" and the IntersectionObserver callback firing.
  const revealMargin = "0px 0px 20% 0px";
  const [introRef, introInView] = useInView(0.05, revealMargin);
  const [storyRef, storyInView] = useInView(0.05, revealMargin);
  const [quoteRef, quoteInView] = useInView(0.05, revealMargin);
  const [valuesRef, valuesInView] = useInView(0.05, revealMargin);
  const [closingRef, closingInView] = useInView(0.05, revealMargin);

  const values = [
    { title: t.landingAboutValue1Title, body: t.landingAboutValue1Body },
    { title: t.landingAboutValue2Title, body: t.landingAboutValue2Body },
    { title: t.landingAboutValue3Title, body: t.landingAboutValue3Body },
  ];

  return (
    <section id="about" className="sp-section" style={{ background: "var(--color-surface)", paddingLeft: 32, paddingRight: 32 }}>
      <div style={{ maxWidth: 1240, margin: "0 auto" }}>
        <div
          ref={introRef}
          className={`sp-two-things sp-reveal-group${introInView ? " is-visible" : ""}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1.1fr 0.9fr",
            gap: 48,
            alignItems: "center",
          }}
        >
          <div className="sp-reveal sp-reveal-left">
            <div className="tag tag-neutral">{t.landingAboutKicker}</div>
            <h2 style={{ marginTop: 10, maxWidth: 500 }}>{t.landingAboutHeading}</h2>
            <p style={{ opacity: 0.85, maxWidth: 520, fontSize: 16 }}>{t.landingAboutBody}</p>
          </div>
          <ProgressiveImage
            src={aboutIntroPhoto}
            alt=""
            loading="lazy"
            imgClassName="washed"
            className="sp-about-photo sp-reveal sp-reveal-right"
            height={340}
            radius={28}
          />
        </div>

        <div className="hr" style={{ margin: "56px auto" }} />

        <div
          ref={storyRef}
          className={`sp-two-things sp-reveal-group${storyInView ? " is-visible" : ""}`}
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "center" }}
        >
          <ProgressiveImage
            src={aboutStoryPhoto}
            alt=""
            loading="lazy"
            imgClassName="washed"
            className="sp-about-photo sp-reveal sp-reveal-left"
            height={320}
            radius={28}
          />
          <div className="sp-reveal sp-reveal-right">
            <div className="tag tag-accent">{t.landingAboutStoryKicker}</div>
            <h3 style={{ marginTop: 10, fontSize: 26, maxWidth: 460 }}>{t.landingAboutStoryHeading}</h3>
            <p style={{ opacity: 0.85 }}>{t.landingAboutStoryBody}</p>
          </div>
        </div>

        <div className="hr" style={{ margin: "56px auto" }} />

        <div
          ref={quoteRef}
          className={`sp-reveal sp-reveal-up sp-reveal-group${quoteInView ? " is-visible" : ""}`}
          style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="var(--color-accent-200)" style={{ margin: "0 auto 16px" }}>
            <path d="M7 11c0-3 2-5 5-5v2c-2 0-3 1-3 3h3v6H4v-4c0-1 1-2 3-2zm10 0c0-3 2-5 5-5v2c-2 0-3 1-3 3h3v6h-8v-4c0-1 1-2 3-2z" />
          </svg>
          <p style={{ fontFamily: "var(--font-heading)", fontSize: 22, lineHeight: 1.5 }}>
            {t.landingAboutBeliefQuote}
          </p>
        </div>

        <div className="hr" style={{ margin: "56px auto" }} />

        <div
          ref={valuesRef}
          className={`sp-cards-grid sp-reveal-group${valuesInView ? " is-visible" : ""}`}
          style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24 }}
        >
          {values.map((v, idx) => (
            <div
              key={v.title}
              className="card elev-sm sp-reveal sp-reveal-up"
              style={{ padding: 26, transitionDelay: `${idx * 0.1}s` }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: "50%",
                  background: "var(--color-accent-100)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-accent-700)" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <div className="card-title" style={{ fontSize: 17 }}>{v.title}</div>
              <p className="card-body">{v.body}</p>
            </div>
          ))}
        </div>

        <div
          ref={closingRef}
          className={`sp-reveal sp-reveal-up sp-reveal-group${closingInView ? " is-visible" : ""}`}
          style={{
            marginTop: 56,
            background: "var(--color-accent-100)",
            borderRadius: "var(--radius-lg)",
            padding: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 20,
          }}
        >
          <div>
            <div style={{ fontFamily: "var(--font-heading)", fontSize: 20 }}>{t.landingAboutClosingHeading}</div>
            <p style={{ opacity: 0.8, maxWidth: 460, margin: "6px 0 0" }}>{t.landingAboutClosingBody}</p>
          </div>
          <button className="btn btn-primary" style={{ padding: "12px 26px", fontSize: 15 }} disabled title="Coming soon">
            {t.landingAboutClosingCta}
          </button>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
